import React, { createContext, FC, ReactNode, useState, useEffect, useCallback } from "react";
import { useHttpUtils } from "../Utilities/useHttpUtils";

export type Privileges = {
    admin?: boolean,
    supervisor?: boolean,
    canProcess?: boolean,
    canReview?: boolean,
    canGrade?: boolean,
    canConfigure?: boolean,
    setBusinessUnitCode?: (code:string)=>void
}

const PrivilegeContext = createContext<Privileges>({});

export const PrivilegeProvider: FC<{ children: ReactNode }> = ({ children }) => {

    const { fetchDataAuthenticated } = useHttpUtils();
    const [businessUnitCode, setBusinessUnitCode] = useState<string>();
    const [admin, setAdmin] = useState<boolean>();
    const [supervisor, setSupervisor] = useState<boolean>();
    const [canProcess, setCanProcess] = useState<boolean>();
    const [canReview, setCanReview] = useState<boolean>();
    const [canGrade, setCanGrade] = useState<boolean>();
    const [canConfigure, setCanConfigure] = useState<boolean>();

    const getAuthenticatedAccountPrivilege = useCallback(async (privilegeName: string, businessUnitCode: string | undefined = undefined) => {
        let accessGranted = false;

        let url = `api/v1/user/checkPrivilege?privilegeName=${privilegeName}`;

        if (businessUnitCode) {
            url += `&businessUnitCode=${businessUnitCode}`;
        }

        await fetchDataAuthenticated(url)
            .then(response => {
                if (!response.ok) {
                    throw new Error(`Failed to check privilege ${privilegeName} for business unit ${businessUnitCode}`);
                }
                return response.json();
            })
            .then(data => {
                accessGranted = !!data;
            })
            .catch(error => console.error(error.message));

        return accessGranted;
    }, [fetchDataAuthenticated]);

    const getPrivileges = useCallback(() => {

        getAuthenticatedAccountPrivilege('AdminPrivilegeName')
            .then(result => setAdmin(result));
        getAuthenticatedAccountPrivilege('CanConfigurePrivilegeName')
            .then(result => setCanConfigure(result));

        if (businessUnitCode) {
            getAuthenticatedAccountPrivilege('SupervisorPrivilegeName', businessUnitCode)
                .then(result => setSupervisor(result));
            getAuthenticatedAccountPrivilege('CanProcessPrivilegeName', businessUnitCode)
                .then(result => setCanProcess(result));
            getAuthenticatedAccountPrivilege('CanReviewPrivilegeName', businessUnitCode)
                .then(result => setCanReview(result));
            getAuthenticatedAccountPrivilege('CanGradePrivilegeName', businessUnitCode)
                .then(result => setCanGrade(result));
        }
    }, [getAuthenticatedAccountPrivilege, businessUnitCode]);

    useEffect(() => {
        getPrivileges();
    }, [getPrivileges]);

    return (
        <PrivilegeContext.Provider value={{ admin, supervisor, canProcess, canReview, canGrade, canConfigure, setBusinessUnitCode }}>
            {children}
        </PrivilegeContext.Provider>
    );
}

export default PrivilegeContext;